// ✅ src/pages/VehicleV2/MaintenanceDetailsPrint.jsx
// Print Page for Maintenance Details (matches handlePrint navigate state)
// Route: /vehicle/maintenanceDetailsPrint

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/maintenanceDetails";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return String(v).trim();
  }
  return fallback;
};

const num = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};

/**
 * Normalize row keys -> UI columns.
 * ✅ Update keys once you see actual API response.
 */
function normalizeRow(r) {
  return {
    date: pick(r, ["Date", "date", "fld_date", "MaintenanceDate", "fld_maintdate"]),
    subAssembly: pick(r, ["SubAssembly", "subassembly", "fld_subassembly", "Sub_Assembly"]),
    description: pick(r, ["Description", "description", "fld_description", "MaintDescription"]),
    workDone: pick(r, ["WorkDone", "workdone", "fld_workdone", "Work_Done", "Remarks", "remark"]),
    vendor: pick(r, ["Vendor", "vendor", "Supplier", "supplier", "fld_vendorname"]),
    billNo: pick(r, ["BillNo", "billno", "InvoiceNo", "invoiceno", "fld_billno"]),
    amount: pick(r, ["Amount", "amount", "Cost", "cost", "Total", "total", "fld_amount"]),
  };
}

export default function MaintenanceDetailsPrint() {
  const navigate = useNavigate();
  const location = useLocation();
  const [qs] = useSearchParams();

  /* ---------------- read EXACT params from handlePrint() ---------------- */
  const filters = useMemo(() => {
    const st = location?.state || {};

    // Optional fallback: sessionStorage (if you want ASPX-style)
    const ss = {
      mainVehicleId: sessionStorage.getItem("mainVehicleId"),
      branch: sessionStorage.getItem("branch"),
      regNo: sessionStorage.getItem("regNo"),
      vehicleName: sessionStorage.getItem("vehicleName"),
      vehicleCode: sessionStorage.getItem("vehicleCode"),
      subAssembly: sessionStorage.getItem("subAssembly"),
      subAssemblyText: sessionStorage.getItem("subAssemblyText"),
      description: sessionStorage.getItem("description"),
      descriptionText: sessionStorage.getItem("descriptionText"),
      fromDate: sessionStorage.getItem("fromDate"),
      toDate: sessionStorage.getItem("toDate"),
      page: sessionStorage.getItem("page"),
      pageSize: sessionStorage.getItem("pageSize"),
      total: sessionStorage.getItem("total"),
    };

    // querystring fallback
    const q = {
      mainVehicleId: qs.get("mainVehicleId"),
      branch: qs.get("branch"),
      regNo: qs.get("regNo"),
      vehicleName: qs.get("vehicleName"),
      vehicleCode: qs.get("vehicleCode"),
      subAssembly: qs.get("subAssembly"),
      subAssemblyText: qs.get("subAssemblyText"),
      description: qs.get("description"),
      descriptionText: qs.get("descriptionText"),
      fromDate: qs.get("fromDate"),
      toDate: qs.get("toDate"),
      page: qs.get("page"),
      pageSize: qs.get("pageSize"),
      total: qs.get("total"),
    };

    // Final merge priority: state -> sessionStorage -> querystring
    return {
      mainVehicleId: toStr(st.mainVehicleId || ss.mainVehicleId || q.mainVehicleId),
      branch: toStr(st.branch || ss.branch || q.branch),
      regNo: toStr(st.regNo || ss.regNo || q.regNo),
      vehicleName: toStr(st.vehicleName || ss.vehicleName || q.vehicleName),
      vehicleCode: toStr(st.vehicleCode || ss.vehicleCode || q.vehicleCode),
      subAssembly: toStr(st.subAssembly || ss.subAssembly || q.subAssembly),
      subAssemblyText: toStr(st.subAssemblyText || ss.subAssemblyText || q.subAssemblyText),
      description: toStr(st.description || ss.description || q.description),
      descriptionText: toStr(st.descriptionText || ss.descriptionText || q.descriptionText),
      fromDate: toStr(st.fromDate || ss.fromDate || q.fromDate),
      toDate: toStr(st.toDate || ss.toDate || q.toDate),
      page: toStr(st.page || ss.page || q.page),
      pageSize: toStr(st.pageSize || ss.pageSize || q.pageSize),
      total: toStr(st.total || ss.total || q.total),
    };
  }, [location?.state, qs]);

  /* ---------------- data ---------------- */
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        setErr("");

        if (!filters.mainVehicleId || !filters.branch) {
          throw new Error("Missing print filters (mainVehicleId / branch). Open print from Maintenance Details page.");
        }

        // ✅ backend should return ALL rows for printing (ignore paging) OR print only current page
        // If you want only current page print -> pass page/pageSize; else omit them.
        const { data } = await axios.get(`${API_BASE}/list`, {
          params: {
            mainVehicleId: filters.mainVehicleId,
            branch: filters.branch,
            regNo: filters.regNo,
            vehicleName: filters.vehicleName,
            vehicleCode: filters.vehicleCode,
            subAssembly: filters.subAssembly,
            description: filters.description,
            fromDate: filters.fromDate,
            toDate: filters.toDate,

            // optional: print current page only
            page: filters.page,
            pageSize: filters.pageSize,
          },
        });

        const list = Array.isArray(data) ? data : data?.rows || data?.data || [];
        if (!Array.isArray(list)) throw new Error("Invalid print response");

        if (alive) setRows(list);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.message || e.message || "Failed to load print data");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [filters]);

  const normRows = useMemo(() => rows.map(normalizeRow), [rows]);

  /* OPTIONAL: mail button (if you have exportPdf) */
  const onMail = async () => {
    try {
      const { data } = await axios.post(`${API_BASE}/exportPdf`, {
        mainVehicleId: filters.mainVehicleId,
        branch: filters.branch,
        regNo: filters.regNo,
        vehicleName: filters.vehicleName,
        vehicleCode: filters.vehicleCode,
        subAssembly: filters.subAssembly,
        description: filters.description,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
      });

      const fileName = data?.fileName;
      if (!fileName) throw new Error("PDF export failed (fileName missing)");
      window.open(`/Mail/mail.aspx?Rpt=${encodeURIComponent(fileName)}`, "_blank", "width=650,height=280");
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Mail/PDF export not configured");
    }
  };

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      <style>{`
        @media print {
          body { margin: 0; padding: 10px; font-size: 8pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; font-size: 7pt; }
          th, td { border: 1px solid #000 !important; padding: 2px !important; }
        }
        @page { size: A4 landscape; margin: 0.3in; }
      `}</style>

      <div className="p-2">
        {/* Top actions */}
        <div className="no-print flex items-center justify-between mb-3">
          <button onClick={() => navigate(-1)} className="px-4 py-2 border rounded hover:bg-gray-50">
            ← Back
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={onMail}
              className="px-4 py-2 border rounded hover:bg-gray-50"
              title="Send mail (requires exportPdf backend + old mail.aspx)"
            >
              ✉️ Mail
            </button>

            <button
              onClick={() => window.print()}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow"
            >
              🖨️ Print
            </button>
          </div>
        </div>

        {/* Loading/Error */}
        {loading ? (
          <div className="no-print p-3 border rounded bg-gray-50 text-sm mb-2">Loading print data...</div>
        ) : err ? (
          <div className="no-print p-3 border rounded bg-red-50 text-sm text-red-700 mb-2">{err}</div>
        ) : null}

        {/* Header */}
        <div className="border-2 border-black mb-2">
          <div className="flex items-center p-2">
            <div className="w-20 h-16 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
                <div className="text-xs">PVT LTD</div>
              </div>
            </div>

            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold">Nissi Infotech</h1>
              <div className="text-sm font-semibold">Nissi</div>
              <div className="text-sm font-semibold">CIVIL CONSTRUCTION</div>
              <div className="text-xs">
                3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
              </div>
              <div className="text-xs">
                Phone No : 9003744644 &nbsp;&nbsp;&nbsp;&nbsp; infracnsoft-implementation@nissiinfotech.in
              </div>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-2 border-2 border-black py-1">
          <h2 className="text-base font-bold">Vehicle Maintenance Details Report</h2>
        </div>

        {/* Filters */}
        <div className="mb-2 border border-black p-1 text-xs">
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            <div>
              <b>Branch:</b> {filters.branch || "-"}
            </div>
            <div>
              <b>Vehicle Code:</b> {filters.vehicleCode || "-"}
            </div>
            <div>
              <b>Vehicle Name:</b> {filters.vehicleName || "-"}
            </div>
            <div>
              <b>Reg No:</b> {filters.regNo || "-"}
            </div>
            <div>
              <b>Sub Assembly:</b> {filters.subAssemblyText || filters.subAssembly || "-"}
            </div>
            <div>
              <b>Description:</b> {filters.descriptionText || filters.description || "-"}
            </div>
            <div>
              <b>Period:</b> {filters.fromDate || "-"} to {filters.toDate || "-"}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border-2 border-black text-xs">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-black px-1 py-1 font-bold text-center">S.No</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Date</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Sub Assembly</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Description</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Work Done / Remarks</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Vendor</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Bill No</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Amount</th>
              </tr>
            </thead>

            <tbody>
              {!loading && normRows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="border border-black px-2 py-2 text-center">
                    No data to print.
                  </td>
                </tr>
              ) : (
                normRows.map((r, i) => (
                  <tr key={i}>
                    <td className="border border-black px-1 py-1 text-center">{i + 1}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.date}</td>
                    <td className="border border-black px-1 py-1">{r.subAssembly}</td>
                    <td className="border border-black px-1 py-1">{r.description}</td>
                    <td className="border border-black px-1 py-1">{r.workDone}</td>
                    <td className="border border-black px-1 py-1">{r.vendor}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.billNo}</td>
                    <td className="border border-black px-1 py-1 text-right">{r.amount}</td>
                  </tr>
                ))
              )}

              {/* Total amount */}
              {normRows.length > 0 && (
                <tr className="bg-gray-100 font-bold">
                  <td className="border border-black px-1 py-1 text-center" colSpan={7}>
                    TOTAL
                  </td>
                  <td className="border border-black px-1 py-1 text-right">
                    {normRows.reduce((a, x) => a + num(x.amount), 0)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-2 text-xs border-2 border-black p-1">
          <p>Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com</p>
        </div>
      </div>
    </div>
  );
}
